// src/lib/recommendationMap.ts

export type MaturityLabel =
  | "Nonexistent"
  | "Initial"
  | "Developing"
  | "Defined/Managed"
  | "Optimized";

export type FunctionKey = "Govern" | "Identify" | "Protect" | "Detect" | "Respond" | "Recover";

type Recs = Record<MaturityLabel, string[]>;
type FunctionRecommendations = Record<FunctionKey, Recs>;

/** Base phrasing reused across all functions to keep tone consistent. */
const BASE: Record<MaturityLabel, string[]> = {
  Nonexistent: [
    "Assign ownership and document basic roles/responsibilities.",
    "Draft a simple policy/standard and communicate it.",
    "Start capturing a minimal register (assets, risks, or tickets).",
  ],
  Initial: [
    "Formalize the ad-hoc process into a repeatable procedure.",
    "Roll out awareness/training to the people doing the work.",
    "Introduce monthly reviews to move towards consistency.",
  ],
  Developing: [
    "Expand coverage (all teams/systems) and close gaps.",
    "Add measurable KPIs and simple dashboards.",
    "Automate obvious checks where possible.",
  ],
  "Defined/Managed": [
    "Integrate metrics into leadership reviews.",
    "Schedule periodic audits/QA and capture findings.",
    "Feed lessons learned into roadmaps and budgets.",
  ],
  Optimized: [
    "Benchmark against peers and best practices.",
    "Continuously tune/automate based on risk and data.",
    "Share patterns and reusable playbooks across teams.",
  ],
};

/** Govern (GV) function overlays */
const GV: Recs = {
  Nonexistent: [
    "Assign a cybersecurity owner and establish accountability to leadership.",
    "Draft a minimal risk policy and communication plan.",
    ...BASE.Nonexistent,
  ],
  Initial: [
    "Form a governance forum (monthly/quarterly) and document risk appetite.",
    "Map regulations and align policies to compliance requirements.",
    ...BASE.Initial,
  ],
  Developing: [
    "Integrate cybersecurity risk into enterprise risk committees.",
    "Track budget, staffing, and strategic initiatives with KPIs.",
    ...BASE.Developing,
  ],
  "Defined/Managed": [
    "Report risk metrics and control effectiveness to the board.",
    "Align governance artifacts with frameworks (NIST, ISO, GSMA).",
    ...BASE["Defined/Managed"],
  ],
  Optimized: [
    "Use risk analytics to drive continuous improvement and investment.",
    "Benchmark governance maturity against industry peers.",
    ...BASE.Optimized,
  ],
};

/** Identify (ID) function overlays */
const ID: Recs = {
  Nonexistent: [
    "Create an asset and data inventory starter sheet (owners + criticality).",
    "Name a risk owner and stand up a basic risk register.",
    ...BASE.Nonexistent,
  ],
  Initial: [
    "Adopt a discovery tool for assets and document data classes.",
    "Tier third parties and begin lightweight risk reviews.",
    ...BASE.Initial,
  ],
  Developing: [
    "Automate asset discovery and reconcile monthly.",
    "Run quarterly risk reviews and update treatment plans.",
    ...BASE.Developing,
  ],
  "Defined/Managed": [
    "Track inventory/risk KPIs (coverage %, time to update, risk closure).",
    "Align governance artifacts to NIST CSF 2.0 and keep versions.",
    ...BASE["Defined/Managed"],
  ],
  Optimized: [
    "Continuously reconcile inventories with network/EDR sources.",
    "Use risk trend data to drive investment decisions.",
    ...BASE.Optimized,
  ],
};

/** Protect (PR) overlays */
const PR: Recs = {
  Nonexistent: [
    "Enforce MFA for admins and remote access immediately.",
    "Stand up basic endpoint protection on all laptops/servers.",
    ...BASE.Nonexistent,
  ],
  Initial: [
    "Document change and access procedures (JML, reviews).",
    "Start monthly patch cycles and track completion.",
    ...BASE.Initial,
  ],
  Developing: [
    "Roll out SSO + MFA to core apps; add PAM for admins.",
    "Segment networks by sensitivity; define deny-by-default rules.",
    ...BASE.Developing,
  ],
  "Defined/Managed": [
    "Measure patch SLAs, EDR coverage, and change rollback success.",
    "Test backups with regular restore validations (RTO/RPO).",
    ...BASE["Defined/Managed"],
  ],
  Optimized: [
    "Adopt risk-based auth and automated patch orchestration.",
    "Continuously validate segmentation and hardening baselines.",
    ...BASE.Optimized,
  ],
};

/** Detect (DE) overlays */
const DE: Recs = {
  Nonexistent: [
    "Centralize logs from key systems (IdP, endpoints, firewalls).",
    "Define a basic alert triage workflow (who/when/how).",
    ...BASE.Nonexistent,
  ],
  Initial: [
    "Tune the noisiest alerts and document severities/SLAs.",
    "Add CTI (threat intel) feed(s) to prioritize what matters.",
    ...BASE.Initial,
  ],
  Developing: [
    "Expand log coverage; map use-cases to MITRE tactics.",
    "Establish on-call rotation and escalation paths.",
    ...BASE.Developing,
  ],
  "Defined/Managed": [
    "Track MTTA/MTTR and rule effectiveness; run weekly tuning.",
    "Add endpoint isolation playbooks from the SIEM/SOAR.",
    ...BASE["Defined/Managed"],
  ],
  Optimized: [
    "Automate enrichment (asset, user, intel) for investigations.",
    "Continuously measure coverage vs. risks and close gaps.",
    ...BASE.Optimized,
  ],
};

/** Respond (RS) overlays */
const RS: Recs = {
  Nonexistent: [
    "Draft an IR plan with roles, contacts, and severity model.",
    "Create a simple comms template for incidents.",
    ...BASE.Nonexistent,
  ],
  Initial: [
    "Run a tabletop exercise for a common scenario (phishing, ransomware).",
    "Define containment and eradication checklists.",
    ...BASE.Initial,
  ],
  Developing: [
    "Stand up an on-call schedule and escalation matrix.",
    "Integrate legal/regulatory notification triggers.",
    ...BASE.Developing,
  ],
  "Defined/Managed": [
    "Measure exercise outcomes and action closure rates.",
    "Practice containment/eradication runbooks quarterly.",
    ...BASE["Defined/Managed"],
  ],
  Optimized: [
    "Automate containment for endpoints and identities where safe.",
    "Share lessons learned org-wide and update playbooks.",
    ...BASE.Optimized,
  ],
};

/** Recover (RC) overlays */
const RC: Recs = {
  Nonexistent: [
    "Document recovery priorities and target RTO/RPO by system tier.",
    "Protect backups (immutable/offline) and test restores.",
    ...BASE.Nonexistent,
  ],
  Initial: [
    "Create recovery runbooks and validation checklists.",
    "Perform a quarterly restore test and record results.",
    ...BASE.Initial,
  ],
  Developing: [
    "Practice staged recovery with sign-offs from owners.",
    "Track recovery KPIs and backlog of improvements.",
    ...BASE.Developing,
  ],
  "Defined/Managed": [
    "Include recovery in incident/tabletop exercises.",
    "Report recovery readiness to leadership quarterly.",
    ...BASE["Defined/Managed"],
  ],
  Optimized: [
    "Automate restore validations for critical datasets.",
    "Benchmark recovery performance and optimize bottlenecks.",
    ...BASE.Optimized,
  ],
};

export const RECOMMENDATIONS_BY_FUNCTION: FunctionRecommendations = {
  Govern: GV,
  Identify: ID,
  Protect: PR,
  Detect: DE,
  Respond: RS,
  Recover: RC,
};

/** Utility: pick recs by function + maturity label */
export function getRecommendations(
  functionKey: FunctionKey,
  label: MaturityLabel
): string[] {
  const byFn = RECOMMENDATIONS_BY_FUNCTION[functionKey];
  if (!byFn) return BASE[label] ?? [];
  return byFn[label] ?? [];
}
